// Labelled native `<select>`. Plan 24.
//
// Accepts either an `options` array (string labels / values) or raw
// `<option>` children for the handful of places that need
// `<optgroup>` / disabled options.

import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react';
import { cn } from '../utils/cn';
import { INPUT_BASE_CLASS, INPUT_DENSITY, type InputDensity } from './FormInput';

export interface FormSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface FormSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  id?: string;
  density?: InputDensity;
  containerClassName?: string;
  options?: ReadonlyArray<FormSelectOption>;
  /** Placeholder option rendered first with an empty value. */
  placeholder?: string;
}

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(function FormSelect(
  {
    label,
    hint,
    error,
    id,
    density = 'comfortable',
    className,
    containerClassName,
    options,
    placeholder,
    children,
    ...rest
  },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedById = hint || error ? `${inputId}-desc` : undefined;

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-bold uppercase text-stone-400 ml-1 tracking-wider block"
        >
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedById}
        className={cn(
          INPUT_BASE_CLASS,
          INPUT_DENSITY[density],
          'appearance-none pr-10',
          error ? 'border-red-200' : 'border-stone-100',
          className
        )}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options
          ? options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))
          : children}
      </select>
      {(hint || error) && (
        <p
          id={describedById}
          className={cn('text-xs ml-1', error ? 'text-red-600' : 'text-stone-500')}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
